import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionMenuItem } from './responsive-action-menu.types';

const COLLAPSE_SAVINGS_PX = 44; // approx width saved by collapsing 2 inline buttons to 1 menu trigger

@Component({
  selector: 'adx-responsive-action-menu',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatMenuModule, MatTooltipModule, FontAwesomeModule, TranslatePipe],
  templateUrl: './responsive-action-menu.component.html',
  styleUrls: ['./responsive-action-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResponsiveActionMenuComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() items: ReadonlyArray<ActionMenuItem> = [];

  @ViewChild('host', { static: true }) hostRef!: ElementRef<HTMLElement>;

  collapsed = false;
  effectiveItems: ReadonlyArray<ActionMenuItem> = [];

  private resizeObserver?: ResizeObserver;
  private wrapperEl: HTMLElement | null = null;
  private lastExpandedRequiredWidth = 0;

  constructor(private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone) {}

  ngOnChanges(): void {
    this.effectiveItems = (this.items ?? []).filter(i => i.visible !== false);
    this.recomputeCollapse();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    this.wrapperEl = this.findWrapper(this.hostRef.nativeElement);
    const target = this.wrapperEl ?? this.hostRef.nativeElement;

    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.zone.run(() => this.recomputeCollapse());
      });
      this.resizeObserver.observe(target);
    });

    // initial measurement after first layout
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.zone.run(() => this.recomputeCollapse()));
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  trackById(_: number, item: ActionMenuItem): string {
    return item.id;
  }

  invoke(item: ActionMenuItem): void {
    item.action();
  }

  private recomputeCollapse(): void {
    const count = this.effectiveItems.length;
    if (count <= 1) {
      if (this.collapsed) {
        this.collapsed = false;
        this.cdr.detectChanges();
      }
      return;
    }

    // Prefer wrapper overflow as the trigger. Fall back to host width if no wrapper.
    if (this.wrapperEl) {
      const wrapper = this.wrapperEl;

      if (!this.collapsed) {
        // In expanded state: remember current width needed (table fills or exceeds wrapper)
        this.lastExpandedRequiredWidth = wrapper.scrollWidth;
        if (wrapper.scrollWidth > wrapper.clientWidth + 1) {
          this.collapsed = true;
          this.cdr.detectChanges();
        }
        return;
      }

      // In collapsed state: un-collapse when wrapper is wide enough to fit the expanded table
      const required = this.lastExpandedRequiredWidth > 0
        ? this.lastExpandedRequiredWidth
        : wrapper.scrollWidth + COLLAPSE_SAVINGS_PX;

      if (wrapper.clientWidth >= required + 4) {
        this.collapsed = false;
        this.cdr.detectChanges();
      }
      return;
    }

    // Fallback: host width heuristic (only reached when no wrapper ancestor is found)
    const hostWidth = this.hostRef?.nativeElement?.clientWidth ?? 0;
    const needed = count * 48;
    const next = hostWidth > 0 && hostWidth < needed && hostWidth >= 40;
    if (next !== this.collapsed) {
      this.collapsed = next;
      this.cdr.detectChanges();
    }
  }

  private findWrapper(start: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = start.parentElement;
    while (el) {
      if (el.classList.contains('adx-data-table-wrapper')) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }
}
